import {Vnode} from 'mithril';
import app from 'flarum/forum/app';
import Modal, {IInternalModalAttrs} from 'flarum/common/components/Modal';
import Select from 'flarum/common/components/Select';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import RequestError from 'flarum/common/utils/RequestError';
import Form from '../models/Form';

const translationPrefix = 'kilowhat-formulaire.forum.export.';

type ExportFormat = 'xlsx' | 'xls' | 'ods' | 'csv' | 'json';
type ExportHeading = 'title' | 'key' | 'none';

const META_OPTIONS = [
    'uid',
    'user_id',
    'user_username',
    'user_displayname',
    'link_id',
    'locked_at',
    'created_at',
    'updated_at',
    'hidden_at',
];

const DEFAULT_META_OPTIONS = [
    'uid',
    'user_username',
    'created_at',
];

function numberedPre(content: string) {
    return m('.FormulaireExportModal-code', [
        m('pre.numbers', content.split('\n').map((line, index) => index + 1).join('\n')),
        m('pre.content', content),
    ]);
}

interface ExportModalAttrs extends IInternalModalAttrs {
    form: Form
}

export default class ExportModal extends Modal<ExportModalAttrs> {
    className() {
        return 'FormulaireModal FormulaireTheme FormulaireExportModal Modal--large';
    }

    title() {
        return app.translator.trans(translationPrefix + 'title');
    }

    format: ExportFormat = 'xlsx'
    heading: ExportHeading = 'title'
    fields: string[] | null = null
    meta: string[] | null = null
    showAllMeta: boolean = false
    optionKeys: boolean = false
    loadingPreview: boolean = false
    preview: { format: ExportFormat, data: string } | null = null;

    oninit(vnode: Vnode) {
        super.oninit(vnode);

        this.updatePreview();
    }

    content() {
        const {form} = this.attrs;

        const metaOptions = [...META_OPTIONS];

        if (form.attribute('canExportUserDetails')) {
            metaOptions.splice(4, 0, 'user_email', 'user_activated');
        }

        return m('.FormulaireExportModal-row', [
            m('.FormulaireExportModal-config.Modal-body', [
                m('.Form-group', m('a', {
                    className: 'Button Button--primary Button--block hasIcon',
                    download: form.private_title() + '.' + this.format,
                    href: this.generateUrl(),
                }, [
                    m('i.icon.fas.fa-file-download.Button-icon'),
                    m('span.Button-label', app.translator.trans(translationPrefix + 'submit')),
                ])),
                m('.Form-group', [
                    m('label', app.translator.trans(translationPrefix + 'format')),
                    Select.component({
                        value: this.format,
                        onchange: (value: ExportFormat) => {
                            this.format = value;

                            this.updatePreview();
                        },
                        options: {
                            xlsx: app.translator.trans(translationPrefix + 'format-options.xlsx'),
                            xls: app.translator.trans(translationPrefix + 'format-options.xls'),
                            ods: app.translator.trans(translationPrefix + 'format-options.ods'),
                            csv: app.translator.trans(translationPrefix + 'format-options.csv'),
                            json: app.translator.trans(translationPrefix + 'format-options.json'),
                        },
                    }),
                ]),
                m('.Form-group', [
                    m('label', app.translator.trans(translationPrefix + 'heading')),
                    Select.component({
                        value: this.heading,
                        onchange: (value: ExportHeading) => {
                            this.heading = value;

                            this.updatePreview();
                        },
                        options: {
                            title: app.translator.trans(translationPrefix + 'heading-options.title'),
                            key: app.translator.trans(translationPrefix + 'heading-options.key'),
                            none: app.translator.trans(translationPrefix + 'heading-options.none'),
                        },
                    }),
                ]),
                m('.Form-group', [
                    m('label', app.translator.trans(translationPrefix + 'fields')),
                    Array.isArray(form.template()) ? form.template()!.map(field => {
                        const checked = this.fields === null || this.fields.indexOf(field.key!) !== -1;

                        return m('div', m('label', [
                            m('input', {
                                type: 'checkbox',
                                checked,
                                onchange: () => {
                                    if (this.fields === null) {
                                        this.fields = form.template()!.map((f: any) => f.key) as string[];
                                    }

                                    if (checked) {
                                        this.fields = this.fields.filter(fk => fk !== field.key);
                                    } else {
                                        this.fields.push(field.key!);
                                    }

                                    if (this.fields.length === form.template()!.length) {
                                        this.fields = null;
                                    }

                                    this.updatePreview();
                                },
                            }),
                            ' ',
                            field.title || field.key,
                        ]));
                    }) : null,
                ]),
                m('.Form-group', [
                    m('label', app.translator.trans(translationPrefix + 'meta')),
                    metaOptions.map(meta => {
                        if (!this.showAllMeta && DEFAULT_META_OPTIONS.indexOf(meta) === -1) {
                            return null;
                        }

                        const checked = (this.meta === null && DEFAULT_META_OPTIONS.indexOf(meta) !== -1) ||
                            (Array.isArray(this.meta) && this.meta.indexOf(meta) !== -1);

                        return m('div', m('label', [
                            m('input', {
                                type: 'checkbox',
                                checked,
                                onchange: () => {
                                    if (this.meta === null) {
                                        this.meta = DEFAULT_META_OPTIONS.slice();
                                    }

                                    if (checked) {
                                        this.meta = this.meta.filter(thisMeta => thisMeta !== meta);
                                    } else {
                                        this.meta.push(meta);
                                    }

                                    if (
                                        this.meta.length === DEFAULT_META_OPTIONS.length &&
                                        this.meta.every(thisMeta => DEFAULT_META_OPTIONS.indexOf(thisMeta) !== -1)
                                    ) {
                                        this.meta = null;
                                    }

                                    this.updatePreview();
                                },
                            }),
                            ' ',
                            app.translator.trans(translationPrefix + 'meta-options.' + meta),
                        ]));
                    }),
                    this.showAllMeta ? null : m('div', m('a', {
                        href: '#',
                        onclick: (event: Event) => {
                            event.preventDefault();
                            this.showAllMeta = true;
                        },
                    }, app.translator.trans(translationPrefix + 'more-meta'))),
                ]),
                m('.Form-group', m('label', [
                    m('input', {
                        type: 'checkbox',
                        checked: this.optionKeys,
                        onchange: () => {
                            this.optionKeys = !this.optionKeys;

                            this.updatePreview();
                        },
                    }),
                    ' ',
                    app.translator.trans(translationPrefix + 'option-keys'),
                ])),
            ]),
            m('.FormulaireExportModal-preview', [
                m('h3', app.translator.trans(translationPrefix + 'preview')),
                this.viewPreview(),
            ]),
        ]);
    }

    viewPreview() {
        if (this.loadingPreview || !this.preview) {
            return LoadingIndicator.component({className: 'LoadingIndicator--block'});
        }

        if (this.preview.format === 'json') {
            return numberedPre(JSON.stringify(JSON.parse(this.preview.data), null, 2));
        } else if (this.preview.format === 'csv') {
            return numberedPre(this.preview.data);
        } else {
            const table = JSON.parse(this.preview.data) as (string | number | boolean | null)[][];

            if (table.length) {
                return m('table', m('tbody', [
                    m('tr', [
                        m('th'),
                        table[0].map((value, index) => m('th', String.fromCharCode(65 + index))),
                    ]),
                    table.map((row, index) => m('tr', [
                        m('th', index + 1),
                        row.map(value => {
                            let displayValue: any = value;

                            if (value === true) {
                                displayValue = app.translator.trans(translationPrefix + 'preview-primitives.boolean-true');
                            } else if (value === false) {
                                displayValue = app.translator.trans(translationPrefix + 'preview-primitives.boolean-false');
                            } else if (typeof value === 'number' && Math.round(value * 10000) !== Math.round(value * 1000) * 10) {
                                // If the value is a number with 4+ decimals, it's a raw Excel date
                                // It's just too complicated to make this work right, so we'll just show a placeholder
                                displayValue = app.translator.trans(translationPrefix + 'preview-primitives.excel-date');
                            }

                            return m('td', displayValue);
                        }),
                    ])),
                ]));
            }

            return 'empty result set';
        }
    }

    generateUrl(preview = false) {
        const queryString: any = {};

        if (this.fields !== null) {
            queryString.fields = this.fields.join(',');
        }

        if (this.meta !== null) {
            queryString.meta = this.meta.join(',');
        }

        if (this.heading !== 'title') {
            queryString.heading = this.heading;
        }

        if (this.optionKeys) {
            queryString.optionKeys = 1;
        }

        if (preview) {
            queryString.preview = 1;
        }

        const downloadQueryString = m.buildQueryString(queryString);

        return app.forum.attribute('apiUrl') + this.attrs.form.apiEndpoint() + '/export.' + this.format + (downloadQueryString ? '?' + downloadQueryString : '');
    }

    updatePreview() {
        this.loadingPreview = true;
        m.redraw(); // In Mithril 0.2, it seems the start/end computation of m.request causes the redraw to be delayed until the end of the request

        // We cache the value before the request
        // Because in case we click a lot of stuff, multiple requests could be pending
        const format = this.format;

        // Not using app.request because there's some odd stuff happening with deserializing
        // In Mithril 2, deserialize: raw => raw does not seem to work despite being the solution proposed in the documentation
        m.request({
            method: 'GET',
            url: this.generateUrl(true),
            extract: (xhr: XMLHttpRequest) => {
                const status = xhr.status;

                // If the request failed, throw the same error as Flarum
                // Unfortunately we cannot easily re-use the error alert, so for now we'll just throw the error
                // So that the preview doesn't say "empty results"
                if (status < 200 || status > 299) {
                    throw new RequestError(status, xhr.responseText, {}, xhr);
                }

                // Contrary to Flarum's app.request, do not automatically parse as JSON
                return xhr.responseText;
            },
        }).then((data: any) => {
            this.loadingPreview = false;
            this.preview = {
                format,
                data,
            };
            m.redraw();
        }).catch((error: any) => {
            this.loadingPreview = false;
            this.preview = null;
            m.redraw();

            throw error;
        });
    }
}
